import { DeadlineMonthTypeEnum } from '@zaoblako/shared/common/enums/deadline.month.type.enum';
import { DeadlineTypeEnum } from '@zaoblako/shared/common/enums/deadline.type.enum';
import { TimeZoneEnum } from '@zaoblako/shared/common/enums/time.zone.enum';
import type { DeadlineEntity } from '@zaoblako/shared/common/entities/deadline.entity';
import type { DeadlineFindInterface } from '@zaoblako/shared/common/dtos/deadline/deadline-find.dto';
import type { DeadlineFormInterface } from '@zaoblako/shared/common/dtos/deadline/deadline-form.dto';
import * as moment from 'moment-timezone';

/** Helper object which provides concrete deadline date calculation by deadline settings. Singleton object. */
export class DeadlineDateCalcualator {
  /** Deadline date calculator instance for whole application */
  private static instance: DeadlineDateCalcualator = null;

  /**
   * Get {@link DeadlineDateCalcualator} instance. Create it if not exist.
   *
   * @returns Deadline date calculator instance
   */
  public static getInstance(): DeadlineDateCalcualator {
    if (!DeadlineDateCalcualator.instance) {
      DeadlineDateCalcualator.instance = new DeadlineDateCalcualator();
    }
    return DeadlineDateCalcualator.instance;
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private constructor() {}

  /**
   * Calculate date for received base date and deadline settings
   *
   * @param deadline Deadline settings ({@link DeadlineEntity}, {@link DeadlineFindDto} or {@link DeadlineFormInterface} type object)
   * @param [baseDate] Base date by default (`Date` object, current date by default)
   * @param [timezone] Time zone to use ({@link TimeZoneEnum.EUROPE_MOSCOW} by defautl)
   *
   * @returns Computed date (`Date` object)
   */
  public calculateDate(
    deadline: DeadlineEntity | DeadlineFindInterface | DeadlineFormInterface,
    baseDate: moment.MomentInput = new Date(),
    timezone = TimeZoneEnum.EUROPE_MOSCOW,
  ): Date {
    const mBaseDate = moment(baseDate)
      .tz(timezone)
      .startOf('day');

    switch (deadline.type) {
      case DeadlineTypeEnum.ABSOLUTE: {
        const baseDate = mBaseDate.clone().startOf('month');
        if (deadline.monthType === DeadlineMonthTypeEnum.NEXT) {
          baseDate.add(1, 'month');
        }
        baseDate.date(
          deadline.dayOfMonth <= baseDate.daysInMonth()
            ? deadline.dayOfMonth
            : baseDate.daysInMonth()
        );
        return baseDate.toDate();
      }
      case DeadlineTypeEnum.RELATIVE:
        return mBaseDate.clone().add(deadline.daysAfter, 'day').toDate();
      default:
        return mBaseDate.toDate();
    }
  }
}
